/**
 * useSpeechRecognition
 *
 * Wraps the Web Speech API SpeechRecognition for voice-to-text input.
 *
 * Design decisions:
 * - Continuous mode OFF: recognition stops automatically after a pause, which
 *   mirrors the UX of every enterprise speech widget (Google Docs, Office 365).
 * - interimResults ON: fires onTranscript(text, false) for live preview while
 *   the user speaks, then onTranscript(text, true) with the committed final text.
 * - Single active session guard: calling start() while already recording is a no-op.
 * - Refs, not state, own the SpeechRecognition instance so re-renders never
 *   recreate it unexpectedly.
 * - Cleanup on unmount: recognition.abort() is called to release the mic.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

// ── Browser-compat shim ───────────────────────────────────────────────────────
interface SpeechRecognitionResultItemLike {
  transcript: string;
}

interface SpeechRecognitionResultLike {
  readonly isFinal: boolean;
  readonly [index: number]: SpeechRecognitionResultItemLike;
}

interface SpeechRecognitionEventLike {
  readonly resultIndex: number;
  readonly results: {
    readonly length: number;
    readonly [index: number]: SpeechRecognitionResultLike;
  };
}

interface SpeechRecognitionErrorEventLike {
  readonly error: string;
}

interface SpeechRecognitionLike {
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  lang: string;
  onstart: (() => void) | null;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onspeechend: (() => void) | null;
  onend: (() => void) | null;
  onerror: ((event: SpeechRecognitionErrorEventLike) => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

type SpeechRecognitionCtor = new () => SpeechRecognitionLike;
type SpeechRecognitionWindow = Window & {
  SpeechRecognition?: SpeechRecognitionCtor;
  webkitSpeechRecognition?: SpeechRecognitionCtor;
};

function getSpeechRecognitionCtor(): SpeechRecognitionCtor | null {
  if (typeof window === 'undefined') return null;
  const speechWindow = window as SpeechRecognitionWindow;
  return (
    speechWindow.SpeechRecognition ??
    speechWindow.webkitSpeechRecognition ??
    null
  );
}

// ── Public types ──────────────────────────────────────────────────────────────
export type SpeechRecognitionStatus = 'idle' | 'recording' | 'processing' | 'error';

export interface SpeechRecognitionError {
  code: string;
  message: string;
}

export interface UseSpeechRecognitionOptions {
  /**
   * Called on every recognition result.
   * @param text     The transcript segment (interim or final).
   * @param isFinal  True when the engine has committed the word(s).
   */
  onTranscript: (text: string, isFinal: boolean) => void;
  /** BCP-47 language tag, defaults to the browser locale. */
  lang?: string;
}

export interface UseSpeechRecognitionReturn {
  /** False on Firefox or any browser without SpeechRecognition support. */
  isSupported: boolean;
  isRecording: boolean;
  status: SpeechRecognitionStatus;
  error: SpeechRecognitionError | null;
  start: () => void;
  stop: () => void;
}

// ── Human-readable error messages ─────────────────────────────────────────────
const ERROR_MESSAGES: Record<string, string> = {
  'not-allowed': 'Microphone permission denied. Allow mic access in browser settings.',
  'no-speech': 'No speech detected. Try again.',
  network: 'Network error during recognition. Check your connection.',
  aborted: 'Recording was cancelled.',
  'audio-capture': 'No microphone found. Please connect a microphone.',
  'service-not-allowed': 'Speech recognition service is not permitted on this page.',
};

function mapError(code: string): SpeechRecognitionError {
  return { code, message: ERROR_MESSAGES[code] ?? `Speech recognition error: ${code}` };
}

// ── Hook ──────────────────────────────────────────────────────────────────────
export function useSpeechRecognition({
  onTranscript,
  lang,
}: UseSpeechRecognitionOptions): UseSpeechRecognitionReturn {
  const Ctor = getSpeechRecognitionCtor();
  const isSupported = Ctor !== null;

  const recogRef = useRef<SpeechRecognitionLike | null>(null);
  const onTranscriptRef = useRef(onTranscript);
  // Keep the callback ref fresh without triggering re-creation of the recognition instance.
  useEffect(() => { onTranscriptRef.current = onTranscript; }, [onTranscript]);

  const [status, setStatus] = useState<SpeechRecognitionStatus>('idle');
  const [error, setError] = useState<SpeechRecognitionError | null>(null);

  // ── Lazily build the SpeechRecognition instance ──────────────────────────
  const ensureRecognition = useCallback((): SpeechRecognitionLike | null => {
    if (!Ctor) return null;
    if (recogRef.current) return recogRef.current;

    const recognition = new Ctor();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;
    if (lang) recognition.lang = lang;

    recognition.onstart = () => {
      setStatus('recording');
      setError(null);
    };

    recognition.onresult = (event: SpeechRecognitionEventLike) => {
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const text = result[0].transcript;
        const isFinal = result.isFinal;
        onTranscriptRef.current(text, isFinal);
      }
    };

    recognition.onspeechend = () => {
      // Browser detected end of speech — transition to processing briefly.
      setStatus('processing');
    };

    recognition.onend = () => {
      setStatus(prev => (prev === 'error' ? 'error' : 'idle'));
    };

    recognition.onerror = (event: SpeechRecognitionErrorEventLike) => {
      const code = event.error;
      // 'aborted' fires when we call recognition.abort() — treat as idle, not an error.
      if (code === 'aborted') {
        setStatus('idle');
        return;
      }
      // 'no-speech' is a soft event — don't surface as a blocking error.
      if (code === 'no-speech') {
        setStatus('idle');
        return;
      }
      setError(mapError(code));
      setStatus('error');
    };

    recogRef.current = recognition;
    return recognition;
  }, [Ctor, lang]);

  // ── start ─────────────────────────────────────────────────────────────────
  const start = useCallback(() => {
    if (!isSupported || status === 'recording') return;
    const recognition = ensureRecognition();
    if (!recognition) return;
    setError(null);
    try {
      recognition.start();
    } catch {
      // InvalidStateError if already started — safe to ignore.
    }
  }, [isSupported, status, ensureRecognition]);

  // ── stop ──────────────────────────────────────────────────────────────────
  const stop = useCallback(() => {
    if (recogRef.current && status === 'recording') {
      // stop() lets the engine finalize the current utterance; abort() cuts immediately.
      recogRef.current.stop();
    }
  }, [status]);

  // ── Cleanup on unmount ────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (recogRef.current) {
        recogRef.current.abort();
        recogRef.current = null;
      }
    };
  }, []);

  return {
    isSupported,
    isRecording: status === 'recording',
    status,
    error,
    start,
    stop,
  };
}
